import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Description as FileIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://test2.planfix-to-syrve.com';

const FilesManager = () => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFolder, setUploadFolder] = useState('');
  
  
  const [backupsDialogOpen, setBackupsDialogOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [currentBackupFile, setCurrentBackupFile] = useState(null);
  const [currentBackupFolder, setCurrentBackupFolder] = useState('');

  
  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/files/folders`);
      if (response.data.success) {
        setFolders(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Помилка завантаження папок');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFiles = useCallback(async (folderName) => {
    if (!folderName) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/files/folder/${encodeURIComponent(folderName)}`);
      if (response.data.success) {
        setFiles(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Помилка завантаження файлів');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBackups = useCallback(async (folderName, fileName) => {
    try {
      setLoadingBackups(true);
      const response = await axios.get(`${API_BASE_URL}/api/files/backups`, {
        params: { folderName, fileName }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Помилка завантаження бекапів');
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  
  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    fetchFiles(folder.name);
  };

  
  const handleBack = () => {
    setSelectedFolder(null);
    setFiles([]);
  };

  
  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/files/download`, {
        params: {
          folderName: selectedFolder.name,
          fileName: fileName
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(`Файл ${fileName} завантажено`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Помилка завантаження файлу');
    }
  };

  
  const handleOpenUploadDialog = (folder) => {
    setUploadFolder(folder.name);
    setUploadFileName('');
    setUploadFile(null);
    setUploadDialogOpen(true);
  };

  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.pdf') || file.name.endsWith('.pdf'))) {
      setUploadFile(file);
      
      setUploadFileName(file.name);
    } else {
      setError('Будь ласка, виберіть файл');
    }
  };

  
  const handleUploadFile = async () => {
    if (!uploadFile) {
      setError('Виберіть файл для завантаження');
      return;
    }

    if (!uploadFileName) {
      setError('Вкажіть назву файлу');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('folderName', uploadFolder);
      formData.append('fileName', uploadFileName);
      
      const response = await axios.post(`${API_BASE_URL}/api/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${uploadFileName} успішно збережено`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadFileName('');
        
        if (selectedFolder && selectedFolder.name === uploadFolder) {
          fetchFiles(uploadFolder);
        }
        fetchFolders();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка завантаження');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Помилка при завантаженні файлу');
    } finally {
      setUploading(false);
    }
  };

  
  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`Ви впевнені, що хочете видалити файл ${fileName}?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/files/delete`, {
        params: {
          folderName: selectedFolder.name,
          fileName: fileName
        }
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${fileName} видалено`);
        fetchFiles(selectedFolder.name);
        fetchFolders();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Помилка видалення файлу');
    }
  };

  
  const handleOpenBackupsDialog = async (folderName, file) => {
    setCurrentBackupFolder(folderName);
    setCurrentBackupFile(file);
    await fetchBackups(folderName, file.name);
    setBackupsDialogOpen(true);
  };

  
  const handleRestoreBackup = async (backupName) => {
    if (!window.confirm(`Відновити файл ${currentBackupFile.name} з бекапу ${backupName}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/files/restore-backup`, {
        folderName: currentBackupFolder,
        fileName: currentBackupFile.name,
        backupName: backupName
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${currentBackupFile.name} відновлено`);
        setBackupsDialogOpen(false);
        fetchFiles(currentBackupFolder);
        fetchFolders();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error restoring backup:', err);
      setError('Помилка відновлення');
    }
  };

  
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date) => {
    if (!date) return 'Невідомо';
    return new Date(date).toLocaleString('uk-UA');
  };

  

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', mt: 8 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Файли залишків тканин та випродажу
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {loading && !selectedFolder ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : selectedFolder ? (
        
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
            <Button variant="outlined" onClick={handleBack}>
              ← До списку
            </Button>
            <Typography variant="h5">
              {selectedFolder.name}
            </Typography>
            <Chip label={`Файлів: ${files.length}`} color="primary" />
            <Button 
              variant="contained" 
              startIcon={<UploadIcon />}
              onClick={() => handleOpenUploadDialog(selectedFolder)}
            >
              Додати файл
            </Button>
          </Box>

          <Grid container spacing={3}>
            {files.map((file) => (
              <Grid item xs={12} sm={6} md={4} key={file.name}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <FileIcon sx={{ color: '#4caf50' }} />
                      <Chip 
                        label={file.name?.split('.').pop() || 'pdf'} 
                        size="small" 
                        color="info" 
                      />
                    </Box>
                    <Typography variant="subtitle1" sx={{ wordBreak: 'break-word', fontWeight: 'bold' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(file.size)} • {formatDate(file.modified)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(file.name)}
                    >
                      Завантажити
                    </Button>
                    <IconButton 
                      size="small" 
                      title="Замінити файл"
                      onClick={() => {
                        setUploadFolder(selectedFolder.name);
                        setUploadFileName(file.name);
                        setUploadFile(null);
                        setUploadDialogOpen(true);
                      }}
                    >
                      <UploadIcon fontSize="small" color="warning" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      title="Історія бекапів"
                      onClick={() => handleOpenBackupsDialog(selectedFolder.name, file)}
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      title="Видалити"
                      onClick={() => handleDeleteFile(file.name)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {files.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">В папці немає файлів</Typography>
              <Button 
                variant="contained" 
                startIcon={<UploadIcon />}
                onClick={() => handleOpenUploadDialog(selectedFolder)}
                sx={{ mt: 2 }}
              >
                Додати файл
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        
        <Grid container spacing={3}>
          {folders.map((folder) => (
            <Grid item xs={12} sm={6} md={4} key={folder.name}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
                onClick={() => handleFolderClick(folder)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FolderIcon sx={{ fontSize: 48, color: '#ffc107' }} />
                    <Box>
                      <Typography variant="h6" component="div">
                        {folder.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Файлів: {folder.files?.length || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<UploadIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenUploadDialog(folder);
                    }}
                  >
                    Додати
                  </Button>
                  {folder.files && folder.files.length > 0 && (
                    <Chip 
                      label={folder.files[0].name} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {uploadFileName && files.some(f => f.name === uploadFileName) 
            ? 'Заміна файлу' 
            : 'Завантаження файлу'}
          <IconButton onClick={() => setUploadDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Папка: <strong>{uploadFolder}</strong>
              {uploadFileName && files.some(f => f.name === uploadFileName) && (
                <><br />Замінюємо: <strong>{uploadFileName}</strong></>
              )}
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Назва файлу:
              </Typography>
              <input
                type="text"
                value={uploadFileName}
                onChange={(e) => setUploadFileName(e.target.value)}
                placeholder="Введіть назву файлу"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </Box>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 1 }}
              startIcon={<UploadIcon />}
            >
              {uploadFile ? uploadFile.name : 'Вибрати файл (.pdf / .pdf)'}
              <input
                type="file"
                hidden
                accept=".pdf,.pdf"
                onChange={handleFileSelect}
              />
            </Button>

            {uploadFile && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Вибрано: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Скасувати
          </Button>
          <Button 
            onClick={handleUploadFile} 
            variant="contained" 
            color={uploadFileName && files.some(f => f.name === uploadFileName) ? 'warning' : 'primary'}
            disabled={!uploadFile || !uploadFileName || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {}
      <Dialog open={backupsDialogOpen} onClose={() => setBackupsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Резервні копії: {currentBackupFile?.name}
          <IconButton onClick={() => setBackupsDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingBackups ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} />
            </Box>
          ) : backups.length === 0 ? (
            <Typography textAlign="center" py={4} color="textSecondary">
              Немає резервних копій для цього файлу
            </Typography>
          ) : (
            <List>
              {backups.map((backup, idx) => (
                <React.Fragment key={backup.name + idx}>
                  <ListItem>
                    <ListItemText
                      primary={backup.name}
                      secondary={`Дата: ${formatDate(backup.date)} • Розмір: ${formatFileSize(backup.size)}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleRestoreBackup(backup.name)}
                      >
                        Відновити
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < backups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupsDialogOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilesManager;
